import { useEffect, useRef, useState, type ReactNode } from 'react';
import { HandshakeIcon, HeartIcon } from '@phosphor-icons/react';
import { BedDouble, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { useImageFallback } from '@voll-intelligence/ui-kit/hooks/use-image-fallback';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { OutOfPolicyGlyph } from '../../policy/out_of_policy_mark.js';
import { OfferBadge } from '../../shared/results_page/offer_badge.js';
import { useNeighborPreload } from '../../shared/use_neighbor_preload.js';
import { useKeyedValue } from '../../shared/use_keyed_value.js';
import { HotelPhotoDots } from '../hotel_photo_dots.js';
import type { HotelFavoriteToggle, HotelOfferView, HotelPhotoView } from '../models.js';
// As classes `hui-hotel-offer-*` são deste arquivo, e ele é consumido fora da
// tela de resultados (o cabeçalho do hotel escolhido e a nota do card de
// quarto). Sem o import aqui, esses dois dependiam de a tela de resultados
// estar montada na mesma página para herdar o estilo.
import './hotel_results.css';

/**
 * O CORAÇÃO do card — botão próprio, IRMÃO do card e nunca filho: o card de
 * resultado é um `<button>` inteiro, e botão dentro de botão é HTML inválido
 * (leitor de tela anuncia um controle só). Quem os junta é o
 * `.hui-hotel-offer-frame` (posicionamento no CSS), e é por isso que o coração
 * só existe quando `favoriteToggle` chega — sem ele o DOM do card é o de
 * sempre, byte a byte.
 *
 * `aria-pressed` carrega o ESTADO; o rótulo diz a AÇÃO do próximo clique
 * ("Favoritar hotel" apagado, "Remover favorito" aceso) com o nome do hotel
 * anexado — numa lista, "Favoritar hotel" sozinho não diz qual.
 */
function HotelFavoriteButton({
  offer,
  toggle,
  disabled = false,
}: {
  readonly offer: HotelOfferView;
  readonly toggle: HotelFavoriteToggle;
  readonly disabled?: boolean;
}): ReactNode {
  const pressed = offer.favorite === true;
  const label = pressed ? toggle.unmarkLabel : toggle.markLabel;
  return (
    <button
      type="button"
      className="hui-hotel-offer-favorite"
      aria-pressed={pressed}
      aria-label={`${label} — ${offer.name}`}
      disabled={disabled}
      onClick={() => {
        toggle.onToggle(offer.id);
      }}
    >
      <HeartIcon aria-hidden="true" weight={pressed ? 'fill' : 'regular'} />
    </button>
  );
}

export type HotelOfferCardProps = {
  offer: HotelOfferView;
  detailsLabel: string;
  selectLabel: string;
  action: 'select' | 'details';
  surface: 'map' | 'plain';
  selected?: boolean;
  readOnly?: boolean;
  busy?: boolean;
  showDetailsButton?: boolean;
  /** Rótulos das setas da faixa de fotos. Só a superfície do MAPA monta a
   *  faixa, então quem não a usa não precisa passar. */
  photoCopy?: HotelOfferPhotoCopy;
  /** O toggle do coração (ver `HotelFavoriteToggle`). Ausente ⇒ nenhum coração
   *  e o DOM do card fica exatamente o de antes — é o gate do caminho
   *  conversacional. */
  favoriteToggle?: HotelFavoriteToggle;
  onHotelSelect(hotelId: string): void;
  onDetailsOpen(hotelId: string): void;
};

export function HotelOfferContent({
  offer,
  showBadges = true,
  surface = 'plain',
  photoCopy,
  agreementId,
}: {
  readonly offer: HotelOfferView;
  readonly showBadges?: boolean;
  /** O `id` da linha do acordo — quem a pede é a linha da lista (#6928), que
   *  a aponta no `aria-describedby`. Ausente, a linha não existe: o card diz o
   *  acordo pelo selo. */
  readonly agreementId?: string;
  readonly surface?: HotelOfferCardProps['surface'];
  readonly photoCopy?: HotelOfferPhotoCopy;
}): ReactNode {
  // Sobre o mapa a foto vira FAIXA no topo, do tamanho do card (`CartaoDoPino`
  // do protótipo, que monta o `Carrossel` em `h-24 w-full`). Nas outras
  // superfícies ela é o quadrado de 64px ao lado do texto — o
  // `CardUnicoDoHotel`, que é um átomo de densidade e mostra uma foto só. Os
  // dois existem mesmo sem foto nenhuma (#3955): sem o slot o texto encosta na
  // borda e o card do pino muda de altura entre hotéis.
  const strip = surface === 'map' && photoCopy ? photoCopy : undefined;
  return (
    <>
      {showBadges && offer.badges.length > 0 && (
        <div className="hui-hotel-offer-badges">
          {/* O anel do selo interrompe a borda do card; sobre o mapa não há borda
              e o card já se destaca por sombra (`[&_[data-slot=badge]]:ring-0`
              no protótipo). */}
          {offer.badges.map((badge) => (
            <OfferBadge key={badge.id} badge={badge} ring={surface !== 'map'} />
          ))}
        </div>
      )}
      {strip && <HotelOfferPhotoStrip photos={offer.photos} copy={strip} />}
      <div className="hui-hotel-offer-main">
        {!strip && <HotelOfferPhoto photo={offer.photos[0]} />}
        <div className="hui-hotel-offer-copy">
          <span className="hui-hotel-offer-name">{offer.name}</span>
          <span className="hui-hotel-offer-price">
            <strong>
              {/* O escudo de fora-da-política vem SEM PALAVRA e colado no
                  preço (protótipo `CardUnicoDoHotel`): é onde a decisão
                  acontece, e a palavra mora no card de linha. */}
              {offer.outOfPolicyLabel && (
                <OutOfPolicyGlyph label={offer.outOfPolicyLabel} title={offer.outOfPolicyReason} />
              )}
              {offer.priceLabel}
            </strong>
            {offer.priceDetail && <small>{offer.priceDetail}</small>}
          </span>
          <HotelOfferMeta offer={offer} />
          {/* Na linha da lista (#6928) o acordo é uma linha discreta, na tinta
              da meta, em vez de um selo sólido. */}
          {agreementId && offer.agreedRateLabel && (
            <p id={agreementId} className="hui-hotel-offer-agreement">
              <HandshakeIcon aria-hidden="true" />
              <span>{offer.agreedRateLabel}</span>
            </p>
          )}
          <HotelOfferExtras offer={offer} />
        </div>
      </div>
    </>
  );
}

/** A linha de meta sob o nome (nota e deslocamento) e, quando há, a condição de
 *  reembolso em linha PRÓPRIA (#3404): "Reembolsável a partir de R$ 650" não
 *  cabe ao lado da distância nos 21rem do card, e quebrar a meta ao meio
 *  partia "1,1 km · 14 min a pé" em duas linhas. */
function HotelOfferMeta({ offer }: { readonly offer: HotelOfferView }): ReactNode {
  const travel = [offer.distanceLabel, offer.travelTimeLabel].filter(Boolean).join(' · ');
  return (
    <>
      {/* Sem nota nem trajeto, a meta não existe: uma caixa vazia seria a
          linha 2 da grade e o "/noite" do preço viraria vão até o reembolso. */}
      {(offer.rating || travel) && (
        <div className="hui-hotel-offer-meta">
          {offer.rating && <HotelOfferRating rating={offer.rating} />}
          {travel && <p>{travel}</p>}
        </div>
      )}
      {offer.refundableLabel && <p className="hui-hotel-offer-refund">{offer.refundableLabel}</p>}
    </>
  );
}

/** Quanto o dedo precisa andar na faixa para contar como deslize, e não como
 *  toque trêmulo. */
const PHOTO_SWIPE_MIN_PX = 40;

export type HotelOfferPhotoCopy = {
  previousPhotoLabel: string;
  nextPhotoLabel: string;
};

/**
 * A faixa de fotos do card do pino — o `Carrossel` do protótipo em outra escala.
 *
 * Setas só no hover, pontos sempre: em toque não existe hover, e os pontos são o
 * único indicador de que há mais de uma foto. Feito à mão porque é um índice e
 * duas setas — uma dependência de carrossel aqui seria peso para `setIndex`.
 *
 * Nenhum texto senta na foto, então não há scrim nem conta de contraste; as
 * setas carregam fundo próprio.
 */
function HotelOfferPhotoStrip({
  photos,
  copy,
}: {
  readonly photos: HotelOfferView['photos'];
  readonly copy: HotelOfferPhotoCopy;
}): ReactNode {
  const [index, setIndex] = useState(0);
  // O dedo passa as fotos (#3108): a ponta do toque de partida, para medir o
  // deslize na chegada. Um deslize que virou foto marca `swiped`, e o `click`
  // de compatibilidade que um navegador de toque possa emitir em seguida é
  // engolido na captura — senão a foto que vira escolheria o hotel junto (o
  // card inteiro é alvo de toque na prévia ancorada no pé).
  const touchStartX = useRef<number | null>(null);
  const swipedRef = useRef(false);
  const photo = photos[Math.min(index, photos.length - 1)];
  const step = (delta: number) => {
    setIndex((current) => (current + delta + photos.length) % photos.length);
  };
  // Um card só na tela (a prévia do pino): as vizinhas baixam já (#5616).
  useNeighborPreload(
    photos.map(({ url }) => url),
    index,
  );
  return (
    <div
      className="hui-hotel-offer-strip"
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(event) => {
        const start = touchStartX.current;
        touchStartX.current = null;
        const end = event.changedTouches[0]?.clientX;
        if (start === null || end === undefined || photos.length < 2) return;
        const delta = end - start;
        if (Math.abs(delta) < PHOTO_SWIPE_MIN_PX) return;
        swipedRef.current = true;
        step(delta < 0 ? 1 : -1);
      }}
      onClickCapture={(event) => {
        if (!swipedRef.current) return;
        swipedRef.current = false;
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <HotelOfferPhoto key={photo?.id} photo={photo} />
      {photos.length > 1 && (
        <>
          <Button
            variant="ghost"
            size="icon-sm"
            className="hui-hotel-offer-strip-previous"
            aria-label={copy.previousPhotoLabel}
            onClick={(event) => {
              // O card do pino inteiro é alvo de clique: sem parar aqui, trocar
              // de foto escolhia o hotel junto.
              event.stopPropagation();
              step(-1);
            }}
          >
            <ChevronLeft aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            className="hui-hotel-offer-strip-next"
            aria-label={copy.nextPhotoLabel}
            onClick={(event) => {
              event.stopPropagation();
              step(1);
            }}
          >
            <ChevronRight aria-hidden="true" />
          </Button>
          <span className="hui-hotel-offer-strip-dots">
            <HotelPhotoDots count={photos.length} current={index} />
          </span>
        </>
      )}
    </div>
  );
}

/**
 * A foto da oferta, com queda macia. Uma `<img>` quebrada dentro de 64px pinta o
 * `alt` cru — o nome inteiro do hotel espremido no quadrado, que empurra a linha
 * e lê como defeito da página, não como foto ausente. No lugar dele vai a mesma
 * queda do protótipo: caixa `muted` com o glifo de cama.
 *
 * O nome acessível sobrevive à queda (`role="img"` + `aria-label`) — quem não vê
 * a foto perde a foto, não a informação. `alt` vazio é decorativo por decisão de
 * quem projetou o dado, e aí a queda também é.
 *
 * Hotel sem foto nenhuma (#3955) cai na mesma caixa, decorativa: não há foto a
 * descrever, e o nome do hotel já está no card.
 */
function HotelOfferPhoto({ photo }: { readonly photo: HotelPhotoView | undefined }): ReactNode {
  // A falha é da URL: a lista reusa a mesma posição entre buscas, e a foto do
  // PRÓXIMO hotel ganha a sua tentativa.
  const image = useImageFallback(photo?.url);
  if (!photo || !image.src) {
    return (
      <span
        className="hui-hotel-offer-photo hui-hotel-offer-photo--failed"
        {...(photo?.alt ? { role: 'img', 'aria-label': photo.alt } : { 'aria-hidden': true })}
      >
        <BedDouble aria-hidden="true" />
      </span>
    );
  }
  return (
    <img
      className="hui-hotel-offer-photo"
      src={image.src}
      alt={photo.alt}
      onError={image.onError}
    />
  );
}

function HotelOfferRating({
  rating,
}: {
  readonly rating: NonNullable<HotelOfferView['rating']>;
}): ReactNode {
  // Estrela CHEIA como ícone (protótipo) e só o número ao lado — o glifo `★`
  // em texto herda a fonte e não fecha a forma. A palavra ("nota 4,2") continua
  // sendo o nome acessível da linha, então quem lê por leitor de tela ouve a
  // frase inteira e quem vê lê o número.
  return (
    <p className="hui-hotel-offer-rating" aria-label={rating.label}>
      <Star className="hui-hotel-offer-star" aria-hidden="true" />
      <span aria-hidden="true">{rating.valueLabel ?? rating.label}</span>
      {/* Sem separador: a contagem já vem entre parênteses (`Nota` do protótipo),
          e o `·` a transformava num segundo assunto na mesma linha. */}
      {rating.reviewsLabel && <span aria-hidden="true">{rating.reviewsLabel}</span>}
    </p>
  );
}

/** A linha de comodidades ANDA no hover em vez de cortar com reticências — mesma
 *  receita do `card_unico` do protótipo: o conteúdo é duplicado no markup e o
 *  trilho corre `-50%`. Mas a duplicata E a animação só entram quando a linha
 *  REALMENTE transborda o espaço dela: mede-se a largura da primeira cópia
 *  contra a do contêiner e, cabendo, renderiza UMA vez — estática e legível, sem
 *  cópia, sem marquee e sem o risco de a lista aparecer em dobro num card largo
 *  (o que o antigo truque de `visibility` remediava). A cópia, quando existe, é
 *  `aria-hidden`: o leitor de tela ouve a lista uma vez. O contêiner ganha
 *  `data-marquee="on"` e é esse marcador que o CSS usa para ligar a animação no
 *  hover (ver `hotel_results.css`, incluindo o desligamento sob
 *  `prefers-reduced-motion`).
 *
 *  Mesma forma do `StopPill` da lista de voos (`flight_leg.tsx`): medir
 *  `scrollWidth`/`clientWidth` num efeito e remedir no reflow com um
 *  `ResizeObserver` — o painel muda de largura ao alternar Lista/Padrão/Mapa e
 *  ao abrir/fechar os filtros, e a linha muda quando a fonte carrega. Sem medição (SSR/jsdom, largura 0), o default é
 *  não transbordar: a linha estática, cortada pelo `overflow`. */
function HotelOfferExtras({ offer }: { readonly offer: HotelOfferView }): ReactNode {
  const containerRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLSpanElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  // A assinatura das comodidades dispara a remedição do CONTEÚDO; o
  // `ResizeObserver` já cobre a mudança de largura do CONTÊINER. Inclui id E
  // rótulo: um card que troca a localidade mantém os ids mas muda o texto — e
  // logo a largura da linha —, mudança que o observer não vê (o contêiner não
  // muda de tamanho, só o conteúdo).
  const amenities = useKeyedValue(
    offer.amenities,
    JSON.stringify(offer.amenities.map((amenity) => [amenity.id, amenity.label])),
  );

  useEffect(() => {
    const container = containerRef.current;
    const lineEl = lineRef.current;
    if (!container || !lineEl || amenities.length === 0) return undefined;
    const measure = () => {
      // Mede a PRIMEIRA cópia contra o contêiner — não o `scrollWidth` do
      // contêiner: com a segunda cópia no DOM, esse `scrollWidth` é o dobro e
      // travaria o marquee ligado mesmo depois de o painel alargar o bastante
      // para a linha caber. 1px de tolerância absorve o arredondamento sub-pixel.
      setOverflowing(lineEl.scrollWidth - container.clientWidth > 1);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    // A linha também: a fonte que chega depois do primeiro render (`font-display: swap`)
    // muda a largura dela sem mexer no contêiner, e a medição ficaria a da fonte reserva.
    observer.observe(lineEl);
    return () => {
      observer.disconnect();
    };
  }, [amenities]);

  if (offer.amenities.length === 0) return null;
  const line = offer.amenities.map((amenity, index) => (
    <span key={amenity.id}>
      {index > 0 && <span aria-hidden="true"> · </span>}
      {amenity.label}
    </span>
  ));
  return (
    <div
      ref={containerRef}
      className="hui-hotel-offer-amenities"
      {...(overflowing ? { 'data-marquee': 'on' } : {})}
    >
      <span className="hui-hotel-offer-amenities-track">
        <span ref={lineRef} className="hui-hotel-offer-amenities-line">
          {line}
        </span>
        {overflowing && <span aria-hidden="true">{line}</span>}
      </span>
    </div>
  );
}

function offerAction(
  offer: HotelOfferView,
  preferred: HotelOfferCardProps['action'],
): HotelOfferCardProps['action'] | undefined {
  if (preferred === 'details' && offer.detailsAvailable) return 'details';
  if (preferred === 'select' && offer.bookable) return 'select';
  if (offer.detailsAvailable) return 'details';
  if (offer.bookable) return 'select';
  return undefined;
}

function activateOffer(
  offer: HotelOfferView,
  action: HotelOfferCardProps['action'] | undefined,
  locked: boolean,
  onHotelSelect: HotelOfferCardProps['onHotelSelect'],
  onDetailsOpen: HotelOfferCardProps['onDetailsOpen'],
): void {
  if (locked || !action) return;
  if (action === 'details') {
    onDetailsOpen(offer.id);
    return;
  }
  onHotelSelect(offer.id);
}

function HotelOfferMapPreview({
  offer,
  detailsLabel,
  surface,
  selected,
  photoCopy,
  action,
  locked,
  onActivate,
}: Pick<HotelOfferCardProps, 'offer' | 'detailsLabel' | 'surface' | 'selected' | 'photoCopy'> & {
  readonly action: HotelOfferCardProps['action'] | undefined;
  readonly locked: boolean;
  readonly onActivate: () => void;
}): ReactNode {
  const inactive = locked || !action;
  return (
    <div
      className="hui-hotel-offer-card"
      aria-current={selected || undefined}
      data-surface={surface}
    >
      <HotelOfferContent offer={offer} surface={surface} {...(photoCopy ? { photoCopy } : {})} />
      {action === 'details' && (
        <div className="hui-hotel-offer-actions">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full"
            aria-label={`${detailsLabel} — ${offer.name}`}
            disabled={inactive}
            onClick={onActivate}
          >
            {detailsLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

export function HotelOfferCard({
  offer,
  detailsLabel,
  selectLabel,
  action,
  surface,
  selected = false,
  readOnly = false,
  busy = false,
  showDetailsButton = false,
  photoCopy,
  favoriteToggle,
  onHotelSelect,
  onDetailsOpen,
}: Readonly<HotelOfferCardProps>): ReactNode {
  const resolvedAction = offerAction(offer, action);
  const actionLabel = resolvedAction === 'details' ? detailsLabel : selectLabel;
  const actionLocked = busy || (readOnly && resolvedAction === 'select');
  const activate = () => {
    activateOffer(offer, resolvedAction, actionLocked, onHotelSelect, onDetailsOpen);
  };

  if (showDetailsButton) {
    return (
      <HotelOfferMapPreview
        offer={offer}
        detailsLabel={detailsLabel}
        surface={surface}
        selected={selected}
        {...(photoCopy ? { photoCopy } : {})}
        action={resolvedAction}
        locked={actionLocked}
        onActivate={activate}
      />
    );
  }

  const card = (
    <button
      type="button"
      className="hui-hotel-offer-card"
      // O card inteiro é o botão, e o `aria-label` substitui o conteúdo para o
      // leitor de tela: a linha de reembolso (#3404) entra no nome para não
      // sumir da leitura. No preview do mapa o botão fica AO LADO do conteúdo,
      // que é lido normalmente, então lá o nome segue só com ação e hotel.
      aria-label={[`${actionLabel} — ${offer.name}`, offer.refundableLabel]
        .filter(Boolean)
        .join(', ')}
      aria-current={selected || undefined}
      data-surface={surface}
      data-bookable={resolvedAction === 'select' ? 'true' : undefined}
      data-hotel-details-trigger={resolvedAction === 'details' ? offer.id : undefined}
      disabled={actionLocked || !resolvedAction}
      onClick={activate}
    >
      <HotelOfferContent offer={offer} surface={surface} />
    </button>
  );
  if (!favoriteToggle) return card;
  // O frame só nasce COM o coração: sem `favoriteToggle` o DOM segue idêntico
  // ao de sempre (nenhum wrapper novo em consumidor que não pediu o toggle).
  // `busy` trava o coração junto com o resto do card — escrever no meio de um
  // turno em voo competiria com a lista prestes a ser substituída. `readOnly`
  // NÃO trava: ele congela a escolha na CONVERSA, e favoritar não escreve nela.
  return (
    <div className="hui-hotel-offer-frame">
      {card}
      <HotelFavoriteButton offer={offer} toggle={favoriteToggle} disabled={busy} />
    </div>
  );
}
